import React, { useCallback, useEffect, useRef, useState } from "react";
import { Boxes, Cloud, HardDrive, MapPinned, Navigation2, Radio } from "lucide-react";
import GlobalFeedback from "../../../global/GlobalFeedback";
import { GlobalModal } from "../../../global/GlobalModal";
import {
  GlobalButton,
  GlobalTextField,
} from "../../../global/forms/GlobalFormFields";
import {
  getStorageSettings,
  requestStorageChangeMfa,
  updateStorageSettings,
  updateGeofencingSettings,
} from "../api/storageSettingsApi";
import "../styles/SyncStoragePanel.css";

const OPTIONS = [
  {
    id: "local",
    title: "Local",
    desc: "Disk under /assets",
    hint: "This server",
    Icon: HardDrive,
  },
  {
    id: "supabase",
    title: "Supabase",
    desc: "Managed online buckets",
    hint: "Cloud",
    Icon: Cloud,
  },
  {
    id: "s3",
    title: "AWS S3",
    desc: "Your Amazon bucket",
    hint: "Object store",
    Icon: Boxes,
  },
];

function networkAwareMessage(err, fallback) {
  const msg = String(err?.message || "").trim();
  if (!msg || /failed to fetch|networkerror|load failed/i.test(msg)) {
    return "Server was briefly restarting after the last change. Wait 2–3 seconds, then try again.";
  }
  return msg || fallback;
}

export default function SyncStoragePanel() {
  const [provider, setProvider] = useState("local");
  const [envDefault, setEnvDefault] = useState("local");
  const [geofencingEnabled, setGeofencingEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingGeofence, setSavingGeofence] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [mfaOpen, setMfaOpen] = useState(false);
  const [pendingProvider, setPendingProvider] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [mfaSending, setMfaSending] = useState(false);
  const [mfaConfirming, setMfaConfirming] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [mfaInfo, setMfaInfo] = useState("");
  const mfaBusyRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getStorageSettings();
      const data = res?.data || {};
      setProvider(data.provider || "local");
      setEnvDefault(data.envDefault || "local");
      setGeofencingEnabled(
        data.geofencingEnabled != null
          ? Boolean(data.geofencingEnabled)
          : data.geofencing?.enabled != null
            ? Boolean(data.geofencing.enabled)
            : true,
      );
    } catch (err) {
      setError(networkAwareMessage(err, "Failed to load storage settings"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const closeMfa = () => {
    if (mfaBusyRef.current || mfaSending || mfaConfirming) return;
    setMfaOpen(false);
    setPendingProvider("");
    setMaskedEmail("");
    setOtp("");
    setMfaError("");
    setMfaInfo("");
  };

  const sendMfaOtp = async (nextProvider) => {
    if (mfaBusyRef.current) return;
    mfaBusyRef.current = true;
    setMfaSending(true);
    setMfaError("");
    setMfaInfo("");
    try {
      const res = await requestStorageChangeMfa(nextProvider);
      const data = res?.data || {};
      setMaskedEmail(data.maskedEmail || "");
      setMfaInfo(res?.message || `OTP sent to ${data.maskedEmail || "admin email"}`);
    } catch (err) {
      setMfaError(networkAwareMessage(err, "Failed to send verification OTP"));
    } finally {
      setMfaSending(false);
      mfaBusyRef.current = false;
    }
  };

  const onSelect = async (next) => {
    if (next === provider || saving || mfaBusyRef.current || mfaOpen) return;
    mfaBusyRef.current = true;
    setError("");
    setSuccess("");
    setPendingProvider(next);
    setOtp("");
    setMfaError("");
    setMfaInfo("");
    setMfaOpen(true);
    setMfaSending(true);
    try {
      const res = await requestStorageChangeMfa(next);
      const data = res?.data || {};
      setMaskedEmail(data.maskedEmail || "");
      setMfaInfo(res?.message || `OTP sent to ${data.maskedEmail || "admin email"}`);
    } catch (err) {
      setMfaError(networkAwareMessage(err, "Failed to send verification OTP"));
    } finally {
      setMfaSending(false);
      mfaBusyRef.current = false;
    }
  };

  const confirmMfa = async () => {
    const code = String(otp || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setMfaError("Enter the 6-digit OTP from the admin email");
      return;
    }
    if (mfaBusyRef.current) return;
    mfaBusyRef.current = true;
    setMfaConfirming(true);
    setSaving(true);
    setMfaError("");
    try {
      const res = await updateStorageSettings(pendingProvider, code);
      const data = res?.data || {};
      setProvider(data.provider || pendingProvider);
      setSuccess(res?.message || `Storage switched to ${pendingProvider}`);
      setMfaOpen(false);
      setPendingProvider("");
      setMaskedEmail("");
      setOtp("");
      setMfaInfo("");
      // Brief lock: writing storage.runtime.json used to restart nodemon mid-click.
      setTimeout(() => {
        mfaBusyRef.current = false;
      }, 1500);
    } catch (err) {
      setMfaError(networkAwareMessage(err, "Failed to verify OTP"));
      mfaBusyRef.current = false;
    } finally {
      setMfaConfirming(false);
      setSaving(false);
    }
  };

  const setGeofencing = async (next) => {
    if (savingGeofence || next === geofencingEnabled) return;
    setSavingGeofence(true);
    setError("");
    setSuccess("");
    try {
      const res = await updateGeofencingSettings(next);
      const data = res?.data || {};
      setGeofencingEnabled(
        data.geofencingEnabled != null
          ? Boolean(data.geofencingEnabled)
          : Boolean(data.geofencing?.enabled),
      );
      setSuccess(
        res?.message ||
          (next
            ? "Geofencing enabled — staff must mark attendance inside premises."
            : "Geofencing disabled — staff can mark attendance from any location."),
      );
    } catch (err) {
      setError(networkAwareMessage(err, "Failed to update geofencing"));
    } finally {
      setSavingGeofence(false);
    }
  };

  const activeOpt = OPTIONS.find((o) => o.id === provider) || OPTIONS[0];
  const ActiveIcon = activeOpt.Icon;
  const pendingOpt = OPTIONS.find((o) => o.id === pendingProvider);
  const destinationsLocked = saving || mfaOpen || mfaSending || mfaConfirming;

  return (
    <div className="sync-storage">
      <div className="sync-storage__mesh" aria-hidden />

      <header className="sync-storage__head">
        <div className="sync-storage__head-main">
          <p className="sync-storage__eyebrow">System</p>
          <h2 className="sync-storage__title">Sync &amp; Storage</h2>
          <p className="sync-storage__sub">
            Route every new upload, then set how far staff can be when they punch in.
          </p>
        </div>
        {!loading ? (
          <div className="sync-storage__signals" aria-label="Current routing">
            <div className="sync-storage__signal">
              <span className="sync-storage__signal-label">Upload path</span>
              <strong>
                <ActiveIcon size={14} strokeWidth={2.2} aria-hidden />
                {activeOpt.title}
              </strong>
            </div>
            <div
              className={`sync-storage__signal${geofencingEnabled ? " is-bound" : " is-free"}`}
            >
              <span className="sync-storage__signal-label">Attendance</span>
              <strong>
                {geofencingEnabled ? (
                  <MapPinned size={14} strokeWidth={2.2} aria-hidden />
                ) : (
                  <Navigation2 size={14} strokeWidth={2.2} aria-hidden />
                )}
                {geofencingEnabled ? "Bound" : "Open"}
              </strong>
            </div>
          </div>
        ) : null}
      </header>

      <GlobalFeedback type="error" text={error} />
      <GlobalFeedback type="success" text={success} />

      {loading ? (
        <div className="sync-storage__loading" aria-busy="true">
          <span className="sync-storage__loading-bar" />
          <p>Loading routing console…</p>
        </div>
      ) : (
        <div className="sync-storage__stack">
          <section className="sync-storage__route" aria-labelledby="sync-storage-provider-title">
            <div className="sync-storage__route-top">
              <div>
                <h3 id="sync-storage-provider-title">Upload destination</h3>
                <p>
                  New files only · env default <em>{envDefault}</em> · MFA required to switch
                </p>
              </div>
              <div className="sync-storage__origin" aria-hidden>
                <Radio size={16} strokeWidth={2} />
                <span>New upload</span>
                <span className="sync-storage__origin-beam" />
              </div>
            </div>

            <div
              className={`sync-storage__destinations sync-storage__destinations--${provider}`}
              role="radiogroup"
              aria-label="Storage provider"
            >
              {OPTIONS.map((opt, index) => {
                const selected = provider === opt.id;
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`sync-storage__dest sync-storage__dest--${opt.id}${selected ? " is-live" : ""}`}
                    style={{ "--ss-i": index }}
                    disabled={destinationsLocked}
                    onClick={() => onSelect(opt.id)}
                  >
                    <span className="sync-storage__dest-glow" aria-hidden />
                    <span className="sync-storage__dest-icon" aria-hidden>
                      <Icon size={26} strokeWidth={1.7} />
                    </span>
                    <span className="sync-storage__dest-meta">
                      <span className="sync-storage__dest-hint">{opt.hint}</span>
                      <span className="sync-storage__dest-title">{opt.title}</span>
                      <span className="sync-storage__dest-desc">{opt.desc}</span>
                    </span>
                    <span className={`sync-storage__dest-state${selected ? " is-on" : ""}`}>
                      {selected ? "Receiving" : "Route here"}
                    </span>
                  </button>
                );
              })}
            </div>

            {mfaSending || saving ? (
              <p className="sync-storage__status">
                {mfaSending ? "Sending verification OTP…" : "Rerouting uploads…"}
              </p>
            ) : null}
          </section>

          <section
            className={`sync-storage__reach${geofencingEnabled ? " is-bound" : " is-free"}`}
            aria-labelledby="sync-geofence-title"
          >
            <div className="sync-storage__reach-stripe" aria-hidden />

            <div className="sync-storage__reach-main">
              <span
                className={`sync-storage__reach-icon${geofencingEnabled ? " is-bound" : " is-free"}`}
                aria-hidden
              >
                {geofencingEnabled ? (
                  <MapPinned size={18} strokeWidth={2} />
                ) : (
                  <Navigation2 size={18} strokeWidth={2} />
                )}
              </span>

              <div className="sync-storage__reach-copy">
                <div className="sync-storage__reach-title-row">
                  <h3 id="sync-geofence-title">Attendance reach</h3>
                  <span
                    className={`sync-storage__reach-badge${geofencingEnabled ? " is-bound" : " is-free"}`}
                  >
                    {geofencingEnabled ? "Geofenced" : "Unrestricted"}
                  </span>
                </div>
                <p>
                  {geofencingEnabled
                    ? "Staff must be inside a premises radius to mark attendance."
                    : "Staff can mark attendance from any location."}
                </p>
              </div>

              <div
                className="sync-storage__stripe-toggle"
                role="radiogroup"
                aria-label="Attendance geofencing"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={geofencingEnabled}
                  className={`sync-storage__stripe-opt${geofencingEnabled ? " is-active" : ""}`}
                  disabled={savingGeofence}
                  onClick={() => setGeofencing(true)}
                >
                  On premises
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!geofencingEnabled}
                  className={`sync-storage__stripe-opt${!geofencingEnabled ? " is-active" : ""}`}
                  disabled={savingGeofence}
                  onClick={() => setGeofencing(false)}
                >
                  Anywhere
                </button>
              </div>
            </div>

            {savingGeofence ? (
              <p className="sync-storage__status sync-storage__status--reach">
                Updating attendance reach…
              </p>
            ) : null}
          </section>
        </div>
      )}

      <GlobalModal
        open={mfaOpen}
        onClose={closeMfa}
        title="Verify storage change"
        description={
          pendingOpt
            ? `Confirm switching uploads to ${pendingOpt.title}. A one-time code was sent to the system admin email${maskedEmail ? ` (${maskedEmail})` : ""}.`
            : "Confirm this storage change with the OTP sent to the admin email."
        }
        size="sm"
        variant="warning"
        closeOnBackdrop={!mfaSending && !mfaConfirming}
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", width: "100%" }}>
            <GlobalButton
              type="button"
              variant="ghost"
              disabled={mfaSending || mfaConfirming}
              onClick={closeMfa}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="ghost"
              disabled={mfaSending || mfaConfirming || !pendingProvider}
              onClick={() => sendMfaOtp(pendingProvider)}
            >
              {mfaSending ? "Sending…" : "Resend OTP"}
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="primary"
              disabled={mfaSending || mfaConfirming || !otp}
              onClick={confirmMfa}
            >
              {mfaConfirming ? "Verifying…" : "Confirm switch"}
            </GlobalButton>
          </div>
        }
      >
        <GlobalFeedback type="error" text={mfaError} />
        <GlobalFeedback type="success" text={mfaInfo} />
        <GlobalTextField
          id="storage-mfa-otp"
          label="OTP"
          value={otp}
          onChange={(v) => setOtp(String(v || "").replace(/\D/g, "").slice(0, 6))}
          placeholder="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          disabled={mfaConfirming || mfaSending}
        />
      </GlobalModal>
    </div>
  );
}
